// src/components/FacturasProfesionales.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import logo from "../assets/LOGO - FOOTER.png";
import { listarUsuarios, listarEspecialidades } from "../api";
import "./FacturaView.css";

// Datos del centro
const CENTRO = {
  nombre: "Teresa Hernandez Cardenas",
  direccion: "Avenida Alcalde Jesús Mantaras, local 5",
  localidad: "Jerez de la Frontera, Cádiz 11405",
  telefono: "+34 601 866 199",
  nif: "77172767Q",
};

const CENTRO1 = {
  nombre: "TE AYUDO",
  direccion: "Avenida Alcalde Jesús Mantaras, local 5",
  localidad: "Jerez de la Frontera, Cádiz 11405",
  telefono: "+34 601 866 199",
};

const DEFAULT_IRPF = 7;

const normCode = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_\s]/g, "-")
    .trim();

const normalizeDni = (dni) => String(dni || "").trim().toUpperCase();

const humanizeCode = (code) => {
  const c = normCode(code);

  if (c === "psicologia") return "Psicología";
  if (c === "psicologia-m") return "Psicología M";
  if (c === "psicologia-g") return "Psicología G";
  if (c === "logopeda") return "Logopedia";
  if (c === "nutricion") return "Nutrición";
  if (c === "administracion") return "administracion";

  return String(code || "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

const getTagClass = (code) => {
  const c = normCode(code);
  if (c.includes("psico")) return "tag-psicologia";
  if (c.includes("logo")) return "tag-logopedia";
  if (c.includes("nutri")) return "tag-nutricion";
  if (c.includes("administracion")) return "tag-administracion";
  return "tag-dinamica";
};

const buildConceptoColaboracion = (mesTexto) =>
  `Cuota por colaboración profesional sanitaria, incluyendo uso compartido de espacios y servicios de apoyo a la actividad asistencial, correspondiente al mes de ${mesTexto}. Operación exenta de IVA conforme al artículo 20.Uno.3 de la Ley 37/1992.`;

const calcularBaseProfesional = (
  items = [],
  specialtyCode = "",
  porcentaje = 0,
) => {
  const code = normCode(specialtyCode);

  const totalEspecialidadTarjeta = items.reduce((acc, p) => {
    const c = normCode(p.specialty_code || p.specialty || "");
    const metodo = String(p.payment_method || "").toLowerCase().trim();

    if (c === code && metodo === "tarjeta") {
      return acc + (Number(p.amount) || 0);
    }

    return acc;
  }, 0);

  return totalEspecialidadTarjeta * (Number(porcentaje || 0) / 100);
};

const getIrpfPct = (profesional) => {
  const raw = profesional?.irpf ?? profesional?.retencion_irpf ?? DEFAULT_IRPF;
  const n = Number(raw);
  return Number.isFinite(n) ? n : DEFAULT_IRPF;
};

const padNumber = (n, size = 4) => String(n).padStart(size, "0");

const getYearFromMes = (fechaMes) =>
  fechaMes ? String(fechaMes).slice(0, 4) : String(new Date().getFullYear());

const getCounterKey = (tipo, year) => `teayudo_facturas_${tipo}_${year}`;

const getNextCounter = (tipo, year) => {
  const saved = Number(localStorage.getItem(getCounterKey(tipo, year)) || 0);
  return saved + 1;
};

const saveCounter = (tipo, year, numero) => {
  localStorage.setItem(getCounterKey(tipo, year), String(numero));
};

const buildNumeroFacturaProfesional = (fechaMes, numero) => {
  const year = getYearFromMes(fechaMes);
  return `PROF-${year}-${padNumber(numero)}`;
};

const buildNumeroDocumentoManual = (fechaMes, tipo = "Factura", numero) => {
  const year = getYearFromMes(fechaMes);
  const prefijo = String(tipo || "").toLowerCase() === "presupuesto" ? "PRE" : "FAC";
  return `${prefijo}-${year}-${padNumber(numero)}`;
};

function FacturaProfesionalCard({
  profesional,
  especialidad,
  base,
  irpfPct,
  irpfImporte,
  total,
  concepto,
  numeroFactura,
  mes,
  facturaRef,
  onDownload,
}) {
  const nombreEspecialidad =
    especialidad?.nombre || humanizeCode(especialidad?.code);

  return (
    <section className="factura-card">
      <div className="factura-card-header">
        <h4>Factura {nombreEspecialidad}</h4>
        <span className={`tag ${getTagClass(especialidad?.code)}`}>
          {nombreEspecialidad}
        </span>
      </div>

      <div className="factura-resumen">
        <p>
          <strong>Profesional:</strong> {profesional.nombre}
        </p>
        <p>
          <strong>DNI:</strong> {profesional.dni || "—"}
        </p>
        <p>
          <strong>Rol:</strong> {profesional.role || "Profesional"}
        </p>
        {profesional.colegiado && (
          <p>
            <strong>Nº colegiado:</strong> {profesional.colegiado}
          </p>
        )}
        <p>
          <strong>Especialidad:</strong> {nombreEspecialidad}
        </p>
        <p>
          <strong>Nº factura:</strong> {numeroFactura}
        </p>
        <p>
          <strong>Porcentaje centro:</strong>{" "}
          {Number(profesional.porcentajeCentro || 0)}%
        </p>
        <p>
          <strong>Base factura:</strong> {base.toFixed(2)} €
        </p>
        <p>
          <strong>IRPF:</strong> {Number(irpfPct || 0)}% (-{irpfImporte.toFixed(2)} €)
        </p>
        <p>
          <strong>Total factura:</strong> {total.toFixed(2)} €
        </p>
      </div>

      <button
        className="btn-pdf"
        onClick={() =>
          onDownload(
            facturaRef,
            `factura_${numeroFactura}_${normCode(
              especialidad?.code || profesional.id,
            )}_${mes || "sin_mes"}.pdf`,
          )
        }
      >
        📄 Descargar factura
      </button>

      <div
        className="factura-container"
        ref={facturaRef}
        style={{ marginTop: "1rem" }}
      >
        <header className="factura-header">
          <img src={logo} alt="Te Ayudo" className="factura-logo" />
          <div className="factura-info">
            <p>{CENTRO.nombre}</p>
            <p>{CENTRO.direccion}</p>
            <p>{CENTRO.localidad}</p>
            <p>Tel: {CENTRO.telefono}</p>
          </div>
        </header>

        <h2 className="factura-title">Factura</h2>

        <div className="factura-details">
          <p>
            <strong>Fecha:</strong> {new Date().toLocaleDateString("es-ES")}
          </p>
          <p>
            <strong>Nº factura:</strong> {numeroFactura}
          </p>
          <p>
            <strong>Destinatario:</strong> {profesional.nombre}
          </p>
          <p>
            <strong>NIF:</strong> {normalizeDni(profesional.dni) || "—"}
          </p>
          <p>
            <strong>Rol:</strong> {profesional.role || "Profesional"}
          </p>
          {profesional.colegiado && (
            <p>
              <strong>Nº colegiado:</strong> {profesional.colegiado}
            </p>
          )}
          <p>
            <strong>Especialidad:</strong> {nombreEspecialidad}
          </p>
          <p>
            <strong>Retención IRPF:</strong> {Number(irpfPct || 0)}%
          </p>
        </div>

        <table className="factura-table">
          <thead>
            <tr>
              <th>Concepto</th>
              <th style={{ textAlign: "right" }}>Importe (€)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{concepto}</td>
              <td style={{ textAlign: "right" }}>{base.toFixed(2)} €</td>
            </tr>
            <tr>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>
                IRPF ({Number(irpfPct || 0)}%)
              </td>
              <td style={{ textAlign: "right" }}>
                -{irpfImporte.toFixed(2)} €
              </td>
            </tr>
          </tbody>
        </table>

        <div className="factura-total">
          Total a pagar: {total.toFixed(2)} €
        </div>

        <footer className="factura-footer">
          <p>
            Factura de servicios profesionales de{" "}
            {nombreEspecialidad.toLowerCase()}
          </p>
          <p>Número de cuenta: ES80 2100 2545 1902 1042 0805</p>
          <p>El pago tiene que efectuarse entre los días 1 y 5 del mes.</p>
        </footer>
      </div>
    </section>
  );
}

export default function FacturasProfesionales({
  mes,
  setMes,
  pagosMes,
  loading,
  error,
}) {
  const [usuarios, setUsuarios] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [metaError, setMetaError] = useState("");

  const [selectedProfesionalId, setSelectedProfesionalId] = useState("");
  const facturaProfesionalRef = useRef();

  const year = getYearFromMes(mes);
  const [numeroProfesional, setNumeroProfesional] = useState(() =>
    getNextCounter("profesional", getYearFromMes(mes)),
  );
  const [numeroFacturaManual, setNumeroFacturaManual] = useState(() =>
    getNextCounter("factura", getYearFromMes(mes)),
  );
  const [numeroPresupuesto, setNumeroPresupuesto] = useState(() =>
    getNextCounter("presupuesto", getYearFromMes(mes)),
  );

  const [aplicaIvaDinamica, setAplicaIvaDinamica] = useState(true);

  const [dinamica, setDinamica] = useState({
    tipo: "Factura",
    fecha: new Date().toISOString().slice(0, 10),
    servicio: "",
    nombre: "",
    nif: "",
    concepto: "",
    base: "",
    profesional_nombre: "",
    profesional_dni: "",
    profesional_rol: "",
    profesional_colegiado: "",
    cuenta: "",
    condicionesPago: "",
  });

  const facturaDinamicaRef = useRef();

  useEffect(() => {
    setNumeroProfesional(getNextCounter("profesional", year));
    setNumeroFacturaManual(getNextCounter("factura", year));
    setNumeroPresupuesto(getNextCounter("presupuesto", year));
  }, [year]);

  useEffect(() => {
    (async () => {
      try {
        setLoadingMeta(true);
        setMetaError("");

        const [usersRes, specsRes] = await Promise.all([
          listarUsuarios({ page: 1, page_size: 500 }),
          listarEspecialidades(),
        ]);

        const users = Array.isArray(usersRes?.items)
          ? usersRes.items
          : Array.isArray(usersRes)
            ? usersRes
            : [];

        const specs = Array.isArray(specsRes?.items)
          ? specsRes.items
          : Array.isArray(specsRes)
            ? specsRes
            : [];

        setUsuarios(users);
        setEspecialidades(specs);
      } catch (e) {
        console.error(e);
        setMetaError(e.message || "Error cargando usuarios/especialidades");
      } finally {
        setLoadingMeta(false);
      }
    })();
  }, []);

  const mesTexto = useMemo(() => {
    if (!mes) return "";
    const d = new Date(`${mes}-01`);
    if (Number.isNaN(d.getTime())) return mes;
    return d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  }, [mes]);

  const profesionales = useMemo(() => {
    return usuarios
      .filter((u) => {
        const role = String(u.role || "").toLowerCase();
        return role !== "admin" && Number(u.specialty_id || 0) > 0;
      })
      .map((u) => {
        const especialidad = especialidades.find(
          (e) => Number(e.id) === Number(u.specialty_id),
        );

        return {
          ...u,
          especialidad,
        };
      })
      .filter((u) => u.especialidad);
  }, [usuarios, especialidades]);

  const profesionalSeleccionado = useMemo(() => {
    return profesionales.find(
      (p) => String(p.id) === String(selectedProfesionalId),
    );
  }, [profesionales, selectedProfesionalId]);

  const facturaProfesional = useMemo(() => {
    if (!profesionalSeleccionado) return null;

    const specialtyCode = profesionalSeleccionado.especialidad?.code || "";
    const porcentajeProfesional = Number(
      profesionalSeleccionado.porcentaje_profesional || 0,
    );
    const porcentajeCentro = 100 - porcentajeProfesional;

    const base = calcularBaseProfesional(
      pagosMes,
      specialtyCode,
      porcentajeCentro,
    );

    const irpfPct = getIrpfPct(profesionalSeleccionado);
    const irpfImporte = base * (irpfPct / 100);
    const total = base - irpfImporte;
    const concepto = buildConceptoColaboracion(mesTexto);
    const numeroFactura = buildNumeroFacturaProfesional(mes, numeroProfesional);

    return {
      ...profesionalSeleccionado,
      specialtyCode,
      porcentajeProfesional,
      porcentajeCentro,
      base,
      irpfPct,
      irpfImporte,
      total,
      concepto,
      numeroFactura,
    };
  }, [profesionalSeleccionado, pagosMes, mesTexto, mes, numeroProfesional]);

  const handleDownloadPDF = async (ref, fileName) => {
    const input = ref?.current;
    if (!input) return false;

    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pageWidth, imgHeight);
    pdf.save(fileName);
    return true;
  };

  const handleDownloadFacturaProfesional = async (ref, fileName) => {
    const descargada = await handleDownloadPDF(ref, fileName);
    if (!descargada) return;

    saveCounter("profesional", year, numeroProfesional);
    setNumeroProfesional((prev) => prev + 1);
  };

  const handleDownloadDocumentoManual = async () => {
    const descargada = await handleDownloadPDF(
      facturaDinamicaRef,
      `${dinamica.tipo.toLowerCase()}_${numeroDocumentoManual}_${mes || "sin_mes"}.pdf`,
    );
    if (!descargada) return;

    if (dinamica.tipo === "Presupuesto") {
      saveCounter("presupuesto", year, numeroPresupuesto);
      setNumeroPresupuesto((prev) => prev + 1);
    } else {
      saveCounter("factura", year, numeroFacturaManual);
      setNumeroFacturaManual((prev) => prev + 1);
    }
  };

  const baseDinamica = Number(dinamica.base) || 0;
  const ivaDinamica = aplicaIvaDinamica ? baseDinamica * 0.21 : 0;
  const totalDinamica = baseDinamica + ivaDinamica;
  const numeroActualManual =
    dinamica.tipo === "Presupuesto" ? numeroPresupuesto : numeroFacturaManual;
  const numeroDocumentoManual = buildNumeroDocumentoManual(
    mes,
    dinamica.tipo,
    numeroActualManual,
  );

  const servicioOptions = useMemo(() => {
    const opts = especialidades.map((esp) => ({
      value: esp.code,
      label: esp.nombre || humanizeCode(esp.code),
    }));

    return [...opts, { value: "otro", label: "Otro" }];
  }, [especialidades]);

  const getProfesionalByServicio = (serv) => {
    const spec = especialidades.find(
      (e) => normCode(e.code) === normCode(serv),
    );
    if (!spec) return null;

    return profesionales.find(
      (u) => Number(u.specialty_id) === Number(spec.id),
    );
  };

  const allLoading = loading || loadingMeta;
  const allError = error || metaError;

  return (
    <div className="cierre-panel facturas-profesionales">
      <div className="pagos-actions">
        <div className="month-filter">
          <label>
            Mes:
            <input
              type="month"
              value={mes}
              onChange={(e) => setMes(e.target.value)}
            />
          </label>
        </div>
      </div>

      {allLoading ? (
        <p>Cargando datos...</p>
      ) : allError ? (
        <p style={{ color: "red" }}>{allError}</p>
      ) : (
        <>
          <h3 className="facturas-title">
            Facturas profesionales mes {mesTexto || mes}
          </h3>

          <section className="factura-card">
            <div className="factura-card-header">
              <h4>Factura profesional dinámica</h4>
              <span className="tag tag-dinamica">Profesional</span>
            </div>

            <div className="factura-profesional-form factura-profesional-form--grid">
              <label>
                Profesional:
                <select
                  value={selectedProfesionalId}
                  onChange={(e) => setSelectedProfesionalId(e.target.value)}
                >
                  <option value="">Seleccionar…</option>
                  {profesionales.map((pro, index) => (
                    <option key={pro.id} value={pro.id}>
                      {pro.nombre} -{" "}
                      {pro.especialidad?.nombre ||
                        humanizeCode(pro.especialidad?.code)}{" "}
                      - {buildNumeroFacturaProfesional(mes, numeroProfesional)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!facturaProfesional ? (
              <p style={{ marginTop: "1rem" }}>
                Selecciona un profesional para generar su factura.
              </p>
            ) : (
              <FacturaProfesionalCard
                profesional={facturaProfesional}
                especialidad={facturaProfesional.especialidad}
                base={facturaProfesional.base}
                irpfPct={facturaProfesional.irpfPct}
                irpfImporte={facturaProfesional.irpfImporte}
                total={facturaProfesional.total}
                concepto={facturaProfesional.concepto}
                numeroFactura={facturaProfesional.numeroFactura}
                mes={mes}
                facturaRef={facturaProfesionalRef}
                onDownload={handleDownloadFacturaProfesional}
              />
            )}
          </section>

          <section className="factura-card">
            <div className="factura-card-header">
              <h4>Factura / Presupuesto personalizado</h4>
              <span className="tag tag-dinamica">
                {dinamica.tipo === "Factura"
                  ? "Otros servicios"
                  : "Presupuesto"}
              </span>
            </div>

            <div className="factura-profesional-form factura-profesional-form--grid">
              <label>
                Tipo:
                <select
                  value={dinamica.tipo}
                  onChange={(e) =>
                    setDinamica((prev) => ({ ...prev, tipo: e.target.value }))
                  }
                >
                  <option value="Factura">Factura</option>
                  <option value="Presupuesto">Presupuesto</option>
                </select>
              </label>

              <label>
                Fecha:
                <input
                  type="date"
                  value={dinamica.fecha}
                  onChange={(e) =>
                    setDinamica((prev) => ({ ...prev, fecha: e.target.value }))
                  }
                />
              </label>

              <label>
                Servicio:
                <select
                  value={dinamica.servicio}
                  onChange={(e) => {
                    const serv = e.target.value;
                    const profesional = getProfesionalByServicio(serv);

                    setDinamica((prev) => ({
                      ...prev,
                      servicio: serv,
                      profesional_nombre: profesional?.nombre || "",
                      profesional_dni: profesional?.dni || "",
                      profesional_rol: profesional?.role || "",
                      profesional_colegiado: profesional?.colegiado || "",
                      concepto:
                        prev.concepto ||
                        (serv && serv !== "otro"
                          ? `Servicios profesionales de ${humanizeCode(serv)}`
                          : ""),
                    }));
                  }}
                >
                  <option value="">Seleccionar…</option>
                  {servicioOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Nombre destinatario:
                <input
                  type="text"
                  value={dinamica.nombre}
                  onChange={(e) =>
                    setDinamica((prev) => ({ ...prev, nombre: e.target.value }))
                  }
                />
              </label>

              <label>
                NIF:
                <input
                  type="text"
                  value={dinamica.nif}
                  onChange={(e) =>
                    setDinamica((prev) => ({ ...prev, nif: e.target.value }))
                  }
                />
              </label>

              <label>
                Concepto:
                <input
                  type="text"
                  value={dinamica.concepto}
                  onChange={(e) =>
                    setDinamica((prev) => ({
                      ...prev,
                      concepto: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Base imponible (€):
                <input
                  type="number"
                  step="0.01"
                  value={dinamica.base}
                  onChange={(e) =>
                    setDinamica((prev) => ({ ...prev, base: e.target.value }))
                  }
                />
              </label>

              <label className="factura-iva-toggle">
                <input
                  type="checkbox"
                  checked={aplicaIvaDinamica}
                  onChange={(e) => setAplicaIvaDinamica(e.target.checked)}
                />
                Aplicar IVA (21%)
              </label>

              <label>
                Profesional:
                <input
                  type="text"
                  value={dinamica.profesional_nombre}
                  onChange={(e) =>
                    setDinamica((prev) => ({
                      ...prev,
                      profesional_nombre: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                DNI profesional:
                <input
                  type="text"
                  value={dinamica.profesional_dni}
                  onChange={(e) =>
                    setDinamica((prev) => ({
                      ...prev,
                      profesional_dni: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Rol profesional:
                <input
                  type="text"
                  value={dinamica.profesional_rol}
                  onChange={(e) =>
                    setDinamica((prev) => ({
                      ...prev,
                      profesional_rol: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Nº colegiado:
                <input
                  type="text"
                  value={dinamica.profesional_colegiado}
                  onChange={(e) =>
                    setDinamica((prev) => ({
                      ...prev,
                      profesional_colegiado: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Número de cuenta:
                <input
                  type="text"
                  value={dinamica.cuenta}
                  onChange={(e) =>
                    setDinamica((prev) => ({ ...prev, cuenta: e.target.value }))
                  }
                  placeholder="ES__ ____ ____ ____ ____ ____"
                />
              </label>

              <label>
                Condiciones de pago:
                <input
                  type="text"
                  value={dinamica.condicionesPago}
                  onChange={(e) =>
                    setDinamica((prev) => ({
                      ...prev,
                      condicionesPago: e.target.value,
                    }))
                  }
                  placeholder="Ej: El pago debe efectuarse entre los días 1 y 5 del mes"
                />
              </label>
            </div>

            <div className="factura-resumen">
              <p>
                <strong>Base:</strong> {baseDinamica.toFixed(2)} €
              </p>
              <p>
                <strong>IVA:</strong>{" "}
                {aplicaIvaDinamica
                  ? `21% - ${ivaDinamica.toFixed(2)} €`
                  : "Exento"}
              </p>
              <p>
                <strong>Total:</strong> {totalDinamica.toFixed(2)} €
              </p>
            </div>

            <button
              className="btn-pdf"
              onClick={handleDownloadDocumentoManual}
              disabled={baseDinamica <= 0 || !dinamica.concepto}
            >
              📄 Descargar {dinamica.tipo.toLowerCase()}
            </button>

            <div
              className="factura-container"
              ref={facturaDinamicaRef}
              style={{ marginTop: "1rem" }}
            >
              <header className="factura-header">
                <img src={logo} alt="Te Ayudo" className="factura-logo" />
                <div className="factura-info">
                  <p>{CENTRO1.nombre}</p>
                  <p>{CENTRO1.direccion}</p>
                  <p>{CENTRO1.localidad}</p>
                  <p>Tel: {CENTRO1.telefono}</p>
                </div>
              </header>

              <h2 className="factura-title">{dinamica.tipo}</h2>

              <div className="factura-details">
                <p>
                  <strong>Fecha:</strong>{" "}
                  {dinamica.fecha
                    ? new Date(`${dinamica.fecha}T12:00:00`).toLocaleDateString("es-ES")
                    : "—"}
                </p>
                <p>
                  <strong>Nº {dinamica.tipo === "Presupuesto" ? "presupuesto" : "factura"}:</strong>{" "}
                  {numeroDocumentoManual}
                </p>
                {dinamica.nombre && (
                  <p>
                    <strong>Destinatario:</strong> {dinamica.nombre}
                  </p>
                )}
                {dinamica.nif && (
                  <p>
                    <strong>NIF:</strong> {dinamica.nif}
                  </p>
                )}
                {dinamica.profesional_nombre && (
                  <p>
                    <strong>Profesional:</strong> {dinamica.profesional_nombre}
                  </p>
                )}
                {dinamica.profesional_dni && (
                  <p>
                    <strong>DNI profesional:</strong> {dinamica.profesional_dni}
                  </p>
                )}
                {dinamica.profesional_rol && (
                  <p>
                    <strong>Rol profesional:</strong> {dinamica.profesional_rol}
                  </p>
                )}
                {dinamica.profesional_colegiado && (
                  <p>
                    <strong>Nº colegiado:</strong> {dinamica.profesional_colegiado}
                  </p>
                )}
                {dinamica.concepto && (
                  <p>
                    <strong>Concepto:</strong> {dinamica.concepto}
                  </p>
                )}
              </div>

              <table className="factura-table">
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th style={{ textAlign: "right" }}>Base (€)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{dinamica.concepto || "Concepto"}</td>
                    <td style={{ textAlign: "right" }}>
                      {baseDinamica.toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ textAlign: "right", fontWeight: "bold" }}>
                      {aplicaIvaDinamica ? "IVA (21%)" : "IVA (exento)"}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {ivaDinamica.toFixed(2)} €
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="factura-total">
                Total: {totalDinamica.toFixed(2)} €
              </div>

              <footer className="factura-footer">
                <p>
                  {dinamica.tipo} de servicios profesionales
                  {dinamica.concepto ? ` – ${dinamica.concepto}` : ""}
                </p>
                {dinamica.cuenta && <p>Número de cuenta: {dinamica.cuenta}</p>}
                {dinamica.condicionesPago && <p>{dinamica.condicionesPago}</p>}
              </footer>
            </div>
          </section>
        </>
      )}
    </div>
  );
}